<template>
  <el-dialog :title="ModalTitle" v-model="shouldShow" width="720px">
    <form>
      <el-row :gutter="8">
        <el-col :md="12" :xs="24">
          <el-select
            v-model="trip_.vehicle_id"
            size="medium"
            filterable
            remote
            value-key="uid"
            :remote-method="fetchCars"
          >
            <template #prefix>Veículo:</template>
            <el-option
              v-for="car in Cars"
              :key="car.uid"
              :label="getCarLabel(car)"
              :value="car.uid"
            />
          </el-select>
        </el-col>
        <el-col :md="12" :xs="24">
          <el-select
            v-model="trip_.driver_id"
            size="medium"
            filterable
            remote
            value-key="uid"
            :remote-method="fetchDrivers"
          >
            <template #prefix>Motorista:</template>
            <el-option
              v-for="driver in Drivers"
              :key="driver.uid"
              :label="driver.employee?.name || 'Motorista'"
              :value="driver.uid"
            />
          </el-select>
        </el-col>
      </el-row>

      <el-select
        v-model="selectedOrderIds"
        size="medium"
        filterable
        remote
        clearable
        value-key="uid"
        :remote-method="fetchOrders"
        @change="addOrder"
      >
        <template #prefix>Pedidos:</template>
        <el-option
          v-for="order in AvailableOrders"
          :key="order.uid"
          :label="getOrderLabel(order)"
          :value="order.uid"
        />
      </el-select>

      <el-table
        :data="SelectedOrders"
        stripe
        class="selected-orders-table"
        :cell-style="() => 'text-align:center;'"
      >
        <el-table-column prop="code" label="#" width="90" />
        <el-table-column label="cliente" min-width="180">
          <template #default="{ row }">
            {{ row?.client?.name || row?.client?.legal?.social_name || "" }}
          </template>
        </el-table-column>
        <el-table-column label="produtos" width="100">
          <template #default="{ row }">
            {{ row.products?.length || 0 }}
          </template>
        </el-table-column>
        <el-table-column label="peso estimado" width="140">
          <template #default="{ row }">
            {{ formatWeight(getOrderEstimatedWeight(row)) }}
          </template>
        </el-table-column>
        <el-table-column label="ações" width="90">
          <template #default="{ row }">
            <el-button
              onlyIcon
              type="danger"
              circle
              @click="removeOrder(row)"
            >
              <i class="el-icon-delete"></i>
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-alert
        type="info"
        :closable="false"
        show-icon
        class="weight-summary"
      >
        <template #title>
          Peso estimado selecionado:
          {{ formatWeight(SelectedEstimatedWeight) }}
        </template>
      </el-alert>
    </form>

    <template #footer>
      <el-row justify="space-between" type="flex">
        <el-button
          type="text"
          class="button"
          @click="shouldShow = false"
          :disabled="isLoadingSave"
          >Cancelar</el-button
        >
        <el-button
          type="text"
          class="button"
          @click="save"
          :loading="isLoadingSave"
          :disabled="isLoadingSave"
          >Salvar</el-button
        >
      </el-row>
    </template>
  </el-dialog>
</template>

<script>
import CarService from "../../services/cars";
import DriverService from "../../services/drivers";
import OrderService from "../../services/orders";
import DeliveryTripService from "../../services/delivery_trips";
import { notifyError, notifySuccess } from "../../utils/notifiers";
import { weightFormatter } from "../../utils/formatters";

export default {
  name: "DeliveryTripModal",
  props: ["trip", "showModal", "linkedOrderIds"],
  emits: ["close-modal", "should-update"],
  data() {
    return {
      trip_: {},
      cars: [],
      drivers: [],
      orders: [],
      selectedOrderIds: null,
      selectedOrders: [],
      isLoadingSave: false,
    };
  },
  watch: {
    trip(v) {
      this.prepareTrip(v);
    },
    'trip.orders': {
      handler(newOrders) {
        this.selectedOrders = newOrders || [];
      },
      immediate: true,
    },
    showModal(v) {
      if (v) {
        this.prepareTrip(this.trip);
        this.fetchCars("");
        this.fetchDrivers("");
        this.fetchOrders("");
      }
    },
  },
  computed: {
    ModalTitle() {
      return this.isNew ? "Cadastrar viagem de entrega" : "Editar viagem";
    },
    isNew() {
      return !this.trip?.uid;
    },
    shouldShow: {
      get() {
        return this.showModal;
      },
      set() {
        this.$emit("close-modal");
      },
    },
    Cars() {
      return this.cars || [];
    },
    Drivers() {
      return this.drivers || [];
    },
    AvailableOrders() {
      const currentOrderIds = this.trip?.orders?.map((order) => order.uid) || [];
      const blocked = this.linkedOrderIds || [];
      const selected = this.trip_.order_ids || [];

      return (this.orders || []).filter(
        (order) =>
          !selected.includes(order.uid) &&
          (currentOrderIds.includes(order.uid) || !blocked.includes(order.uid))
      );
    },
    SelectedOrders() {
      return this.selectedOrders || (this.trip?.orders || []);
    },
    SelectedEstimatedWeight() {
      return this.SelectedOrders.reduce((total, order) => {
        return total + this.getOrderEstimatedWeight(order);
      }, 0);
    },
  },
  methods: {
    prepareTrip(trip) {
      this.selectedOrderIds = null;
      this.trip_ = {
        ...(trip || {}),
        vehicle_id: trip?.vehicle?.uid,
        driver_id: trip?.driver?.uid,
        order_ids: trip?.orders?.map((order) => order.uid) || [],
      };

      if (trip?.vehicle) this.cars = [trip.vehicle];
      if (trip?.driver) this.drivers = [trip.driver];
      if (trip?.orders) this.orders = trip.orders;
    },
    async fetchCars(searchQuery = "") {
      const { cars } = await CarService().index({
        can_deliver: true,
        searchQuery,
      });

      if (cars) this.cars = cars;
    },
    async fetchDrivers(searchQuery = "") {
      const { drivers } = await DriverService().index({
        can_deliver: true,
        searchQuery,
      });

      if (drivers) this.drivers = drivers;
    },
    async fetchOrders(searchName = "") {
      const { orders } = await OrderService().index({
        status: "delivery,ready",
        searchName,
        limit: 50,
        pagination: 1,
      });

      if (orders) {
        const rows = orders.data || orders;
        const selected = this.trip?.orders || [];
        const merged = [...selected, ...rows];
        this.orders = merged.filter(
          (order, index, list) =>
            list.findIndex((item) => item.uid === order.uid) === index
        );
      }
    },
    addOrder(orderUid) {
      if (!orderUid) return;

      if (!this.trip_.order_ids) this.trip_.order_ids = [];
      if (!this.trip_.order_ids.includes(orderUid))
        this.trip_.order_ids.push(orderUid);

      this.selectedOrders.push(
        this.orders.find((order) => order.uid === orderUid)
      );

      this.selectedOrderIds = null;
    },
    removeOrder(order) {
      this.trip_.order_ids = (this.trip_.order_ids || []).filter(
        (orderUid) => orderUid !== order.uid
      );

      this.selectedOrders = this.selectedOrders.filter(
        (selectedOrder) => selectedOrder.uid !== order.uid
      );
    },
    save() {
      this.isLoadingSave = true;
      return this.isNew ? this.createTrip() : this.updateTrip();
    },
    async createTrip() {
      const { trip, error } = await DeliveryTripService().create(this.trip_);

      this.afterSave(trip, error);
    },
    async updateTrip() {
      const { trip, error } = await DeliveryTripService(this.trip_.uid).update(
        this.trip_
      );

      this.afterSave(trip, error);
    },
    afterSave(trip, error) {
      if (trip) {
        notifySuccess(trip.message);
        this.$emit("should-update");
        this.$emit("close-modal");
      } else notifyError(error?.message);

      this.isLoadingSave = false;
    },
    getCarLabel(car) {
      return car?.name || `${car?.model || ""} ${car?.pane || ""}`.trim();
    },
    getDriverLabel(driver) {
      return driver?.employee?.name || driver?.user?.name || "Motorista";
    },
    getOrderLabel(order) {
      return `#${order?.code || ""} - ${order?.client?.name || "Cliente"}`;
    },
    getOrderEstimatedWeight(order) {
      return (order.products || []).reduce(
        (sum, item) =>
          sum + Number(item.weight || 0),
        0
      );
    },
    formatWeight(value) {
      return `${weightFormatter.format(Number(value || 0))} kg`;
    },
  },
};
</script>

<style scoped>
.el-select {
  width: 100%;
  margin-top: 8px;
}

.weight-summary {
  margin-top: 12px;
}

.selected-orders-table {
  margin-top: 12px;
}
</style>
